import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  FlowMediaPlaylistsDto,
  MediaPlaylistDto,
  SessionMediaPlaylistsDto,
} from '@/types/dtos/media/media-playlist.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { NON_SLOT_FLOW_PLAYLIST_SCOPES } from '@/types/dtos/media/flow-playlist-scope.enum';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { SessionSummaryDto } from '@/types/dtos/sessions/response/session-summary.dto';
import type { GetSessionsDto } from '@/types/dtos/sessions/response/get-sessions.dto';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import {
  GET_CONTRIBUTOR_SUMMARY_QUERY_KEY,
  GET_USER_CONTRIBUTORS_QUERY_KEY,
} from '@/state/queries/contributors/user-contributors';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';

type WithFlowPlaylists = {
  media?: MediaDto[];
  mediaPlaylists?: FlowMediaPlaylistsDto;
};

type EventSessionsPage =
  | GetSessionsDto
  | GetSessionsDto[]
  | [GetSessionsDto | GetSessionsDto[], GetSessionsDto | GetSessionsDto[]];

/** WS playlist payloads are lean (no embedded media). Fill from entity.media / prior refs. */
export function hydratePlaylistRefs(
  playlist: MediaPlaylistDto,
  mediaFallback: MediaDto[] | undefined,
): MediaPlaylistDto {
  if (!playlist.refs.length) {
    return playlist;
  }

  const mediaById = new Map<string, MediaDto>();
  for (const media of mediaFallback ?? []) {
    mediaById.set(media.id, media);
  }
  for (const ref of playlist.refs) {
    if (ref.media) {
      mediaById.set(ref.media.id, ref.media);
    }
  }

  let changed = false;
  const refs = playlist.refs.map((ref) => {
    if (ref.media) {
      return ref;
    }
    const fromFallback = mediaById.get(ref.mediaId);
    if (!fromFallback) {
      return ref;
    }
    changed = true;
    return { ...ref, media: fromFallback };
  });

  return changed ? { ...playlist, refs } : playlist;
}

export function upsertMediaInList(mediaList: MediaDto[] | undefined, media: MediaDto): MediaDto[] {
  if (!mediaList?.length) {
    return [media];
  }
  if (mediaList.some((item) => item.id === media.id)) {
    return mediaList;
  }
  return [...mediaList, media];
}

function collectMediaFromPlaylist(
  playlist: MediaPlaylistDto | undefined,
  mediaList: MediaDto[] | undefined,
): MediaDto[] {
  let next = mediaList ?? [];
  for (const ref of playlist?.refs ?? []) {
    if (ref.media) {
      next = upsertMediaInList(next, ref.media);
    }
  }
  return next;
}

/** Merge WS lean playlist into existing — keep media on refs and user-specific capabilities. */
function mergePlaylistUpdate(existing: MediaPlaylistDto | undefined, updated: MediaPlaylistDto): MediaPlaylistDto {
  if (!existing) {
    return updated;
  }
  const existingByUsage = new Map(existing.refs.map((ref) => [ref.usageId, ref]));
  const existingByMediaId = new Map(
    existing.refs.filter((ref) => ref.media).map((ref) => [ref.mediaId, ref.media!] as const),
  );
  const hasVirtualMediaUpdate = updated.virtualMedia !== undefined;
  const virtualMediaCapabilities = hasVirtualMediaUpdate
    ? {
        canEdit: existing.virtualMediaCapabilities?.canEdit ?? false,
        canToggleEnabled:
          existing.virtualMediaCapabilities?.canToggleEnabled ?? existing.virtualMediaCapabilities?.canEdit ?? false,
        usesStoredVirtualMedia: true,
      }
    : (updated.virtualMediaCapabilities ?? existing.virtualMediaCapabilities);

  return {
    ...existing,
    ...updated,
    refs: updated.refs.map((ref) => {
      const prev = existingByUsage.get(ref.usageId);
      const media = ref.media ?? prev?.media ?? existingByMediaId.get(ref.mediaId);
      return prev ? { ...prev, ...ref, media } : media ? { ...ref, media } : ref;
    }),
    permissions: updated.permissions?.length ? updated.permissions : existing.permissions,
    virtualMedia: hasVirtualMediaUpdate ? updated.virtualMedia : existing.virtualMedia,
    effectiveVirtualMedia:
      updated.effectiveVirtualMedia !== undefined ? updated.effectiveVirtualMedia : existing.effectiveVirtualMedia,
    effectiveVirtualItems:
      updated.effectiveVirtualItems !== undefined ? updated.effectiveVirtualItems : existing.effectiveVirtualItems,
    virtualMediaCapabilities,
    applicableVirtualMedia: updated.applicableVirtualMedia ?? existing.applicableVirtualMedia,
    infoFallbackSlug: updated.infoFallbackSlug !== undefined ? updated.infoFallbackSlug : existing.infoFallbackSlug,
    infoFallbackPlaySlug:
      updated.infoFallbackPlaySlug !== undefined ? updated.infoFallbackPlaySlug : existing.infoFallbackPlaySlug,
    infoFallbackPlayAs:
      updated.infoFallbackPlayAs !== undefined ? updated.infoFallbackPlayAs : existing.infoFallbackPlayAs,
    infoFallbackPlayImageUrl:
      updated.infoFallbackPlayImageUrl !== undefined
        ? updated.infoFallbackPlayImageUrl
        : existing.infoFallbackPlayImageUrl,
  };
}

function replacePlaylistInFlowPlaylists(
  mediaPlaylists: FlowMediaPlaylistsDto,
  updated: MediaPlaylistDto,
  mediaFallback?: MediaDto[],
): FlowMediaPlaylistsDto | null {
  let found = false;
  const next: FlowMediaPlaylistsDto = { ...mediaPlaylists };
  const apply = (playlist: MediaPlaylistDto | undefined) =>
    hydratePlaylistRefs(mergePlaylistUpdate(playlist, updated), mediaFallback);

  for (const scope of NON_SLOT_FLOW_PLAYLIST_SCOPES) {
    if (next[scope]?.id === updated.id || (!next[scope] && updated.scope === scope)) {
      next[scope] = apply(next[scope]);
      found = true;
    }
  }

  if (updated.scope === 'slot' && updated.slotId) {
    const slots = { ...(next.slots ?? {}) };
    const existing = slots[updated.slotId];
    if (existing?.id === updated.id || !existing) {
      slots[updated.slotId] = apply(existing);
      next.slots = slots;
      found = true;
    }
  } else if (next.slots) {
    const slots = { ...next.slots };
    for (const [slotId, playlist] of Object.entries(slots)) {
      if (playlist?.id === updated.id) {
        slots[slotId] = apply(playlist);
        found = true;
      }
    }
    next.slots = Object.keys(slots).length > 0 ? slots : undefined;
  }

  return found ? next : null;
}

function patchEntityWithFlowPlaylists<T extends WithFlowPlaylists>(entity: T, updated: MediaPlaylistDto): T {
  const mediaPlaylists = entity.mediaPlaylists
    ? replacePlaylistInFlowPlaylists(entity.mediaPlaylists, updated, entity.media)
    : updated.scope !== 'session'
      ? replacePlaylistInFlowPlaylists({}, updated, entity.media)
      : null;
  if (!mediaPlaylists) {
    return entity;
  }

  let nextMedia = entity.media;
  for (const scope of NON_SLOT_FLOW_PLAYLIST_SCOPES) {
    nextMedia = collectMediaFromPlaylist(mediaPlaylists[scope], nextMedia);
  }
  if (mediaPlaylists.slots) {
    for (const playlist of Object.values(mediaPlaylists.slots)) {
      nextMedia = collectMediaFromPlaylist(playlist, nextMedia);
    }
  }

  return {
    ...entity,
    media: nextMedia !== entity.media ? nextMedia : entity.media,
    mediaPlaylists,
  };
}

function patchSessionPlaylists(session: SessionDto | SessionSummaryDto, updated: MediaPlaylistDto) {
  if (updated.scope !== 'session') {
    return session;
  }

  const existing = session.mediaPlaylists?.session;
  if (existing && existing.id !== updated.id) {
    return session;
  }

  const merged = hydratePlaylistRefs(mergePlaylistUpdate(existing, updated), session.media);
  const mediaPlaylists: SessionMediaPlaylistsDto = { session: merged };
  const nextMedia = collectMediaFromPlaylist(merged, session.media);

  return {
    ...session,
    media: nextMedia !== session.media ? nextMedia : session.media,
    mediaPlaylists,
  };
}

function patchSessionDtoPlaylists(session: SessionDto, updated: MediaPlaylistDto): SessionDto {
  return patchSessionPlaylists(session, updated) as SessionDto;
}

function patchFlowSlotSessionPlaylists(flow: FlowDto, updated: MediaPlaylistDto): FlowDto {
  if (!flow.slots?.length || updated.scope !== 'session') {
    return flow;
  }
  let changed = false;
  const slots: FlowSlotDto[] = flow.slots.map((slot): FlowSlotDto => {
    if (typeof slot.content !== 'object' || slot.content == null || !('mediaPlaylists' in slot.content)) {
      return slot;
    }
    const session = slot.content as SessionDto;
    const patched = patchSessionDtoPlaylists(session, updated);
    if (patched === session) {
      return slot;
    }
    changed = true;
    return { ...slot, content: patched };
  });
  return changed ? { ...flow, slots } : flow;
}

function patchFlowDocument(flow: FlowDto, updated: MediaPlaylistDto): FlowDto {
  let next = patchEntityWithFlowPlaylists(flow, updated);
  next = patchFlowSlotSessionPlaylists(next, updated);
  return next;
}

function patchFlowQueryData(data: FlowDto | FlowDto[], updated: MediaPlaylistDto): FlowDto | FlowDto[] {
  if (Array.isArray(data)) {
    return data.map((flow) => patchFlowDocument(flow, updated));
  }
  return patchFlowDocument(data, updated);
}

function patchGetSessionsPage(page: GetSessionsDto, updated: MediaPlaylistDto): GetSessionsDto {
  return {
    ...page,
    data: page.data?.map((session) => patchSessionPlaylists(session, updated) as SessionDto) ?? page.data,
  };
}

function patchEventSessionsPage(page: EventSessionsPage, updated: MediaPlaylistDto): EventSessionsPage {
  if (Array.isArray(page)) {
    return page.map((entry) => {
      if (Array.isArray(entry)) {
        return entry.map((nested) => patchGetSessionsPage(nested, updated));
      }
      return patchGetSessionsPage(entry, updated);
    }) as EventSessionsPage;
  }
  return patchGetSessionsPage(page, updated);
}

function patchEventSessionsQueryData(data: unknown, updated: MediaPlaylistDto): unknown {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if ('pages' in data && Array.isArray((data as InfiniteData<EventSessionsPage>).pages)) {
    const infinite = data as InfiniteData<EventSessionsPage>;
    return {
      ...infinite,
      pages: infinite.pages.map((page) => patchEventSessionsPage(page, updated)),
    };
  }

  if (Array.isArray(data)) {
    return (data as SessionDto[]).map((session) => patchSessionPlaylists(session, updated) as SessionDto);
  }

  return data;
}

/** Control tab session media list reads eventSessionsPlan; keep playlists in sync. */
function patchEventStoreSessions(updatedPlaylist: MediaPlaylistDto) {
  if (updatedPlaylist.scope !== 'session') {
    return;
  }

  useEventStore.setState((state) => {
    const patchSessions = (sessions?: SessionDto[]) => {
      if (!sessions?.length) {
        return sessions;
      }
      let changed = false;
      const next = sessions.map((session) => {
        const patched = patchSessionPlaylists(session, updatedPlaylist);
        if (patched !== session) {
          changed = true;
        }
        return patched as SessionDto;
      });
      return changed ? next : sessions;
    };

    const eventSessionsPlan = patchSessions(state.eventSessionsPlan);
    const eventSessionsOrganize = patchSessions(state.eventSessionsOrganize);
    if (eventSessionsPlan === state.eventSessionsPlan && eventSessionsOrganize === state.eventSessionsOrganize) {
      return state;
    }

    return {
      eventSessionsPlan,
      eventSessionsOrganize,
      eventSessions: eventSessionsPlan ?? state.eventSessions,
    };
  });
}

/** Flow detail UI reads playlists from Zustand; keep it in sync with playlist mutations. */
function patchFlowStoreWithPlaylist(updatedPlaylist: MediaPlaylistDto) {
  useFlowStore.setState((state) => {
    const patchFlow = (flow?: FlowDto) => {
      if (!flow) {
        return flow;
      }
      return patchFlowDocument(flow, updatedPlaylist);
    };

    const flow = patchFlow(state.flow);
    const flowPlan = patchFlow(state.flowPlan);
    const flowOrganize = patchFlow(state.flowOrganize);

    if (flow === state.flow && flowPlan === state.flowPlan && flowOrganize === state.flowOrganize) {
      return state;
    }

    return { flow, flowPlan, flowOrganize };
  });
}

export function patchMediaPlaylistInQueryCache(queryClient: QueryClient, updatedPlaylist: MediaPlaylistDto) {
  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto | FlowDto[]>({
    queryKey: [GET_FLOW_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchFlowQueryData(data, updatedPlaylist));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto[]>({
    queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
  })) {
    if (!data || !Array.isArray(data)) continue;
    queryClient.setQueryData(
      queryKey,
      data.map((flow) => patchFlowDocument(flow, updatedPlaylist)),
    );
  }

  for (const [queryKey, data] of queryClient.getQueriesData<SessionDto>({
    queryKey: [GET_SESSION_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchSessionPlaylists(data, updatedPlaylist));
  }

  for (const [queryKey, data] of queryClient.getQueriesData({
    queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchEventSessionsQueryData(data, updatedPlaylist));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<ContributorSummaryDto>({
    queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, {
      ...data,
      flows: data.flows?.map((flow) => patchFlowDocument(flow as FlowDto, updatedPlaylist)),
      sessions: data.sessions?.map((session) => patchSessionPlaylists(session, updatedPlaylist)),
    });
  }

  for (const [queryKey, data] of queryClient.getQueriesData<{ data?: ContributorSummaryDto[] }>({
    queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY],
  })) {
    if (!data?.data) continue;
    queryClient.setQueryData(queryKey, {
      ...data,
      data: data.data.map((contributor) => ({
        ...contributor,
        flows: contributor.flows?.map((flow) => patchFlowDocument(flow as FlowDto, updatedPlaylist)),
        sessions: contributor.sessions?.map((session) => patchSessionPlaylists(session, updatedPlaylist)),
      })),
    });
  }

  patchFlowStoreWithPlaylist(updatedPlaylist);
  patchEventStoreSessions(updatedPlaylist);
}
